import type { SaveTakeoffMeasuresRequest } from '@obraplan/core/application/contracts/rate-average';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { isApartmentService } from '@obraplan/core/domain/takeoff/apartment-average';
import { serviceElement } from '@obraplan/core/domain/takeoff/applies-to';
import { summarizeGeometry } from '@obraplan/core/domain/takeoff/geometry';
import { descendsToRooms, roomMeasure } from '@obraplan/core/domain/takeoff/room-quantity';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { geometriaDaTipologia, medidosDaTipologia } from './medido-da-tipologia';
import { fundacaoDaTipologia } from './resumo-dos-itens';
import { floorsOf, type TypologyDraft, type WorkspaceDraft } from './typology-draft';

/**
 * A foto da medida que a bancada grava (Fase 40, etapa 1 — RN-516, ADR-182).
 *
 * A conta da planta mora no navegador (Fases 31 e 35): o servidor não tem a bancada, com as
 * divisas, a estrutura e as vizinhas. A tela fotografa, por tipologia, o medido de cada serviço
 * do apartamento — o mesmo número que o índice multiplica (`medidosDaTipologia`) — e as vezes que
 * a tipologia se repete na obra (`floorsOf`). O servidor faz a média das fotos das obras marcadas.
 *
 * A tipologia vai mesmo sem medida nenhuma: a lista vazia apaga a foto de antes, e o serviço que
 * saiu da tipologia sai da média com ele.
 *
 * Desde a etapa 3 vai também o volume da laje de cada disciplina que compra o aço dela (RN-532):
 * é o que a taxa de aço multiplica, e o peso de cada obra na média da taxa.
 */
export type FotoDaMedida = SaveTakeoffMeasuresRequest['unitTypes'];

/** Seis casas, como a coluna (`0091`): a foto que volta igual não sobe de novo. */
const seis = (valor: number): number => Math.round(valor * 1e6) / 1e6;

/** O id que o servidor deu à tipologia — a que só existe no navegador não tem foto. */
const ID_DO_SERVIDOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function fotoDaMedida(
  workspace: WorkspaceDraft,
  recorteDe: (typology: TypologyDraft) => ServiceCatalog,
): FotoDaMedida {
  return workspace.typologies.flatMap((typology) => {
    if (!ID_DO_SERVIDOR.test(String(typology.unitTypeId))) return [];
    const catalog = recorteDe(typology);
    const doApartamento = catalog.disciplines.flatMap((disciplina) =>
      disciplina.services.filter((servico) => isApartmentService(servico)),
    );
    const geometria =
      doApartamento.length === 0 ? undefined : geometriaDaTipologia(workspace, typology);
    const totais = geometria === undefined ? undefined : summarizeGeometry(geometria);
    const medidos =
      totais === undefined
        ? {}
        : medidosDaTipologia(catalog, totais, typology, fundacaoDaTipologia(workspace, typology));
    const volumeDaLaje = totais?.slab.volume ?? 0;
    const steelVolumes = catalog.disciplines.flatMap((disciplina) =>
      disciplina.services.some(
        (servico) => servico.measureBy === 'ACO' && serviceElement(servico) === 'LAJE',
      ) &&
      Number.isFinite(volumeDaLaje) &&
      volumeDaLaje > 0
        ? [{ disciplineKey: normalizeName(disciplina.name), volume: seis(volumeDaLaje) }]
        : [],
    );
    // A medida de cada cômodo nos serviços de ambiente (etapa 2, RN-525): a média por tipo de
    // cômodo soma a dos cômodos do tipo. O cômodo vai mesmo sem medida: a lista apaga a de antes.
    const doAmbiente = doApartamento.filter((servico) => descendsToRooms(servico));
    const vivos = new Set(
      typology.rooms.filter((room) => room.deletedAt === undefined).map((room) => String(room.id)),
    );
    const rooms = (geometria?.rooms ?? [])
      .filter((comodo) => vivos.has(String(comodo.roomId)))
      .map((comodo) => ({
        roomId: String(comodo.roomId),
        measures: doAmbiente.flatMap((servico) => {
          const medida = roomMeasure(servico, comodo);
          return medida === undefined || !Number.isFinite(medida) || medida < 0
            ? []
            : [{ serviceKey: servico.key, measured: seis(medida) }];
        }),
      }));
    const measures = doApartamento.flatMap((servico) => {
      const medido = medidos[servico.key];
      return medido === undefined || !Number.isFinite(medido) || medido < 0
        ? []
        : [{ serviceKey: servico.key, measured: seis(medido) }];
    });
    return [
      {
        unitTypeId: String(typology.unitTypeId),
        times: floorsOf(workspace, typology),
        measures,
        rooms,
        steelVolumes,
      },
    ];
  });
}

/** A identidade da foto, para gravar só quando a medida mudou. */
export function chaveDaFoto(foto: FotoDaMedida): string {
  return JSON.stringify(
    [...foto]
      .sort((a, b) => a.unitTypeId.localeCompare(b.unitTypeId))
      .map((tipologia) => ({
        ...tipologia,
        measures: [...tipologia.measures].sort((a, b) => a.serviceKey.localeCompare(b.serviceKey)),
        rooms: [...(tipologia.rooms ?? [])].sort((a, b) => a.roomId.localeCompare(b.roomId)),
        steelVolumes: [...(tipologia.steelVolumes ?? [])].sort((a, b) =>
          a.disciplineKey.localeCompare(b.disciplineKey),
        ),
      })),
  );
}
